import {
  copyToClipboard,
  job as jobConstants,
  openProtocolUrl
} from './index';
import {
  setScenarioKeepForever,
  toggleJenkinsBuildForever,
  triggerBaselineUpdate,
  triggerJobFromId,
  triggerWhatsIn,
} from '../actions';
import {
  aws as awsHelpers,
  params as paramHelpers,
  jobNames,
  artifactHealth,
  scenarios,
  urls as urlHelpers,
  utils
} from '../helpers';
import { toastr } from 'react-redux-toastr';

/**
 * Generate additional relevant job actions for child jobs.
 * @param {*} context The relavant parts of state and additional data needed.
 * @returns array of 'job actions'
 */
export const _getChildJobOpenUrlActions = ({
  jobRelationshipsByParentJobId,
  paramsByJobId,
  parentJobId
}) => {
  const childJobs = jobRelationshipsByParentJobId[parentJobId];
  const childIds = childJobs && childJobs.length > 0 ?
    childJobs.map(child => child.child_job_id) :
    [];

  const childParamsSets = childIds.length > 0 ?
    childIds.map(id => paramsByJobId[id]) :
    [];

  const result = [];

  for (const childParams of childParamsSets) {
    // * Case/Engine Metrics specific, only those jobs with a JENKINS_CASE_TYPE param.
    if (childParams && childParams[paramHelpers.JENKINS_CASE_TYPE]) {
      if (childParams[paramHelpers.JENKINS_BUILD_URL]) {
        const link = childParams[paramHelpers.JENKINS_BUILD_URL];
        result.push(
          {
            title: `Open Child Job URL (${childParams[paramHelpers.JENKINS_CASE_TYPE]})`,
            onSelect: () => _openJobUrl(link),
            isDisabled: false
          }
        );
      }
    }
  }

  return result;
};

// ====================================
// Functions to carry out intended actions for when dropdown item is selected
// ====================================

export const _getScenarioJobUrl = (
  runnerJob,
  paramsByJobIds,
  siblingScenarios
) => {
  if (!runnerJob) {
    toastr.error(
      'Failed to open link',
      'Missing expected values, could not generate a valid url'
    );
    return;
  }

  const params = paramsByJobIds[runnerJob.id];

  let link = '';
  if (params && params[paramHelpers.JENKINS_BUILD_URL]) {
    // get the self-reported job link
    link = params[paramHelpers.JENKINS_BUILD_URL];
  }
  else if (siblingScenarios) {
    // try to generate using any self-reported sibling's params
    const foundSiblingUrls = siblingScenarios.map(siblingScenario => {
      const siblingParams = paramsByJobIds[siblingScenario.runner];
      if (!(siblingParams && siblingParams[paramHelpers.JENKINS_JOB_URL])) {
        return undefined;
      }

      return siblingParams[paramHelpers.JENKINS_JOB_URL];
    }).filter(url => url);

    if (foundSiblingUrls.length > 0) {
      link = `${foundSiblingUrls[0]}${runnerJob.number}`;
    }
  }

  return link;
};

export const _openScenarioJobUrl = (
  runnerJob,
  paramsByJobIds,
  siblingScenarios
) => {
  return () => {
    const link =
      _getScenarioJobUrl(runnerJob, paramsByJobIds, siblingScenarios);
    _openJobUrl(link);
  };
};


export const _openJobUrl = (
  link,
  errorMessage = 'Not enough data, could not generate a valid url'
) => {
  if (link) {
    window.open(link, '_blank');
  }
  else {
    toastr.error(
      'Failed to open link',
      errorMessage
    );
  }
};

export const _openNodeIssueJobUrl = (server, jobName, jobNumber, jobFolder) => {
  return () => {
    window.open(jobFolder ?
      `https://${server}/job/${jobFolder}/job/${jobName}/${jobNumber}` :
      `https://${server}/job/${jobName}/${jobNumber}`,
    '_blank');
  };
};

export const _openJiraTicketUrl = (issueTypeId) => {
  return () => {
    window.open(`${urlHelpers.JIRA_BASE_URL}/browse/${issueTypeId}`,
      '_blank');
  };
};

export const _copyFilePath = (serverBotName, jobName, jobNumber, dispatcherParams) => {
  return () => {
    const filePath = utils.stringToBool(dispatcherParams[paramHelpers.JENKINS_USE_S3]) ?
      `s3://${awsHelpers.BUCKETS.MAGNETSTSBUILDARCHIVES}/` +
      `workspaces/${serverBotName}/${jobName}/builds/${jobNumber}` :
      `${jobConstants.WS_PREFIX}\\${serverBotName}\\${jobName}\\builds\\${jobNumber}`;
    copyToClipboard(filePath);
  };
};

export const _openS3Path = (serverBotName, jobName, jobNumber, archiveFolder = 'builds', suffix = '') => {
  return () => {
    const filePath = `https://us-east-1.console.aws.amazon.com/s3/buckets/${awsHelpers.BUCKETS.MAGNETSTSBUILDARCHIVES}?region=us-east-1` +
      `&bucketType=general&prefix=workspaces/${serverBotName}/${jobName}/${archiveFolder}/${jobNumber}${suffix}/&showversions=false`;
    _openJobUrl(filePath);
  };
};

export const _copyArtifactHealthDiffFilePath = (platform, appName,
  supportedVersion, diffVersion) => {
  return () => {
    copyToClipboard(
      `${artifactHealth.ARTIFACT_HEALTH_DIFF_PATH}` +
      `\\${platform}_${appName}_${supportedVersion}_${diffVersion}`
    );
  };
};

export const _copyTreeFilePath = (serverBotName, jobName, jobNumber) => {
  return () => {
    copyToClipboard(
      `${jobConstants.WS_PREFIX}\\${serverBotName}\\` +
      `${jobName}\\builds\\${jobNumber}\\product_tree`
    );
  };
};

export const _copyMetricsFilePath = (serverBotName, jobNumber, dispatcherParams) => {
  return () => {
    // assuming the s3 path we build
    const filePath = utils.stringToBool(dispatcherParams[paramHelpers.JENKINS_USE_S3]) ?
      `s3://${awsHelpers.BUCKETS.MAGNETSTSBUILDARCHIVES}/workspaces/${serverBotName}/${jobNames.PERFORMANCE}/builds/${jobNumber}/Metrics` :
      `${jobConstants.WS_PREFIX}\\${serverBotName}\\${jobNames.PERFORMANCE}\\builds\\${jobNumber}\\Metrics`;
    copyToClipboard(filePath);
  };
};

export const _copyPerformanceMetricsFilePath = (
  serverBotName,
  jobName,
  jobNumber,
  dispatcherParams
) => {
  return () => {
    // assuming the s3 path we build
    const filePath = utils.stringToBool(dispatcherParams[paramHelpers.JENKINS_USE_S3]) ?
      `s3://${awsHelpers.BUCKETS.MAGNETSTSBUILDARCHIVES}/workspaces/${serverBotName}/${jobName}/builds/${jobNumber}` :
      `${jobConstants.WS_PREFIX}\\${serverBotName}\\${jobName}\\builds\\${jobNumber}`;
    copyToClipboard(filePath);
  };
};

export const _copyTextFilePath = (text) => {
  return () => {
    if (text) {
      copyToClipboard(`${text}`);
    }
  };
};

export const _startJob = (id) => {
  return triggerJobFromId(id);
};

export const _openDiffViewer = (type, serverBotName, jobName, jobNumber, dispatcherParams) => {
  return () => {
    if (utils.stringToBool(dispatcherParams[paramHelpers.JENKINS_USE_S3]))
    {
      const startPart = `magnet-sts-build-archives/workspaces/${serverBotName}/${jobName}/builds`;
      const url = `${startPart}/${jobNumber}/ncf/n2n/diffresult/Artifacts/IEFDiff.mfdiff`;
      openProtocolUrl('s3mfdiff', url);
    }
    else
    {
      const casePath = type.startsWith('n') ? 'ncf' : 'ccf';
      const resultPath = `${casePath}\\${type}\\diffresult\\Artifacts\\IEFDiff.mfdiff`;
      const url = `${jobConstants.WS_PREFIX}\\${serverBotName}\\` +
      `${jobName}\\builds\\${jobNumber}\\${resultPath}`;
      openProtocolUrl('mfdiff', url);
    }
  };
};

export const _copyCaseMetricsCaseFilePath = (
  image,
  serverBotName,
  jobName,
  jobNumber,
  dispatcherParams
) => {
  return () => {
    const filePath = utils.stringToBool(dispatcherParams[paramHelpers.JENKINS_USE_S3]) ?
      `s3://${awsHelpers.BUCKETS.MAGNETSTSBUILDARCHIVES}/workspaces/${serverBotName}/${jobName}/${image}/${jobNumber}/case` :
      `${jobConstants.WS_PREFIX}\\${serverBotName}\\${jobName}\\${image}\\${jobNumber}`;
    copyToClipboard(filePath);
  };
};

export const _openCaseMetricsDiffViewer = (
  type,
  image,
  serverBotName,
  jobName,
  jobNumber,
  dispatcherParams
) => {
  return () => {
    if (utils.stringToBool(dispatcherParams[paramHelpers.JENKINS_USE_S3]))
    {
      const resultPath = 'diff/Artifacts/IEFDiff.mfdiff';
      const url = `magnet-sts-build-archives/workspaces/${serverBotName}/${jobName}/${image}/${jobNumber}/${resultPath}`;
      openProtocolUrl('s3mfdiff', url);
    }
    else
    {
      const casePath = type.startsWith('n') ? 'ief_ncf_case' : 'ief_ccf_case';
      const resultPath = `${casePath}\\diff_results\\Artifacts\\IEFDiff.mfdiff`;
      const url = `${jobConstants.WS_PREFIX}\\${serverBotName}\\${jobName}\\${image}\\${jobNumber}\\${resultPath}`;
      openProtocolUrl('mfdiff', url);
    }
  };
};

export const _openArtHealthDiffViewer = (
  platform, appName, supportedVersion, diffVersion) => {
  return () => {
    const resultPath = `${artifactHealth.ARTIFACT_HEALTH_DIFF_PATH}` +
    `\\${platform}_${appName}_${supportedVersion}_${diffVersion}\\diff\\Artifacts\\IEFDiff.mfdiff`;
    openProtocolUrl('mfdiff', resultPath);
  };
};

export const _copyCaseMetricsDiffFilePath = (
  type,
  image,
  serverBotName,
  jobName,
  jobNumber,
  dispatcherParams
) => {
  return () => {
    const casePath = type.startsWith('n') ? 'ief_ncf_case' : 'ief_ccf_case';
    const filePath = utils.stringToBool(dispatcherParams[paramHelpers.JENKINS_USE_S3]) ?
      `s3://${awsHelpers.BUCKETS.MAGNETSTSBUILDARCHIVES}/workspaces/${serverBotName}/${jobName}/${image}/${jobNumber}/diff` :
      `${jobConstants.WS_PREFIX}\\${serverBotName}\\${jobName}\\${image}\\${jobNumber}\\${casePath}\\diff_results`;
    copyToClipboard(filePath);
  };
};

export const _openCaseMetricsDiffS3Path = (
  image,
  serverBotName,
  jobName,
  jobNumber
) => {
  return () => {
    const filePath = `https://us-east-1.console.aws.amazon.com/s3/buckets/${awsHelpers.BUCKETS.MAGNETSTSBUILDARCHIVES}?region=us-east-1` +
      `&bucketType=general&prefix=workspaces/${serverBotName}/${jobName}/${image}/${jobNumber}/diff/&showversions=false`;
    _openJobUrl(filePath);
  };
};

export const _keepBuildForever = (jobName, server, jobNumber, scenariosKeptForever) => {
  return () => {
    if (!scenariosKeptForever[`${jobName}_${jobNumber}`]) {
      toggleJenkinsBuildForever(jobName, server, jobNumber);
      setScenarioKeepForever(`${jobName}_${jobNumber}`, true);
    }
  };
};

export const _dontKeepBuildForever =
  (jobName, server, jobNumber, scenariosKeptForever) => {
    return () => {
      if (scenariosKeptForever[`${jobName}_${jobNumber}`]) {
        toggleJenkinsBuildForever(jobName, server, jobNumber);
        setScenarioKeepForever(`${jobName}_${jobNumber}`, false);
      }
    };
  };

export const _whatsInClick = (params, product) => {
  return triggerWhatsIn(
    params[paramHelpers.PREV_VERSION_BY_PRODUCT(product)],
    params[paramHelpers.VERSION_BY_PRODUCT(product)],
    product
  );
};

export const _setAsBaseline = (scenarioName, jobNumber) => {
  return triggerBaselineUpdate(
    scenarios.getArtifactPlatform(scenarioName),
    scenarios.getArtifactScenarioName(scenarioName),
    jobNumber
  );
};

// ====================================
// Functions to help support isDisabled checks
// ====================================

export const hasEnoughJenkinsInfo = (server, jobName, jobNumber) => {
  return server && jobName && jobNumber;
};


// WEBPACK FOOTER //
// src/helpers/actionItem.js